import React, { Fragment, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import styles from './RichMessageContent.module.css';

/** Word document icon (matches FaRegFileWord) */
function WordIcon({ size = 14, color = '#2b579a' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 384 512" fill={color}>
      <path d="M369.9 97.9L286 14C277 5 264.8-.1 252.1-.1H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V131.9c0-12.7-5.1-25-14.1-34zM332.1 128H256V51.9l76.1 76.1zM48 464V48h160v104c0 13.3 10.7 24 24 24h104v288H48zm220.1-208c-5.7 0-10.6 4-11.7 9.5-20.6 97.7-20.4 95.4-21 103.5-.2-4.6-.4-9.1-1.3-13.5l-25.4-84.2c-2.3-7.6-9.3-12.8-17.2-12.8h-3.3c-8 0-15 5.2-17.2 12.8l-25.4 84.2c-.9 3-1.1 6.1-1.3 9.2-.4-6.7-.2-5.5-21-103.5-1.1-5.5-6-9.5-11.7-9.5H104c-7.6 0-13.3 6.8-11.7 14.2l39.5 163.7c1.5 6.2 7.1 10.6 13.5 10.6h3.7c7.6 0 14.3-5 16.5-12.2l24.9-84.6c.7-2.2 1-4.6 1.4-6.9.5 2.5.8 5.1 1.6 7.5l23.8 84c2.1 7.1 8.7 12.2 16.3 12.2h3.7c6.4 0 12-4.4 13.5-10.6l39.5-163.7c1.6-7.5-4.1-14.3-11.7-14.3h-7.5z" />
    </svg>
  );
}

type MediaSegment =
  | { type: 'markdown'; content: string }
  | { type: 'image' | 'video' | 'audio' | 'pdf' | 'pptx' | '3d' | 'document'; url: string };

const MEDIA_TOKEN_REGEX = /\[media:(image|video|audio|pdf|pptx|3d|document)\]\s+(https?:\/\/[^\s<>"')\]]+|\/[^\s<>"')\]]+|data:[^\s<>"')\]]+)/gi;

/** Clean up matched media URLs — trim trailing text that got captured after file extension */
function cleanMediaUrl(url: string): string {
  // Strip trailing text that got captured after a known media file extension
  const extMatch = url.match(/^(.*\.(?:png|jpe?g|gif|webp|svg|avif|ico|bmp|tiff?|pdf|pptx?|docx?|xlsx?|mp4|mp3|wav|ogg|webm|mov|avi|obj|glb|gltf|stl|fbx))(\s[A-Za-z].*)?$/i);
  if (extMatch && extMatch[2]) {
    return extMatch[1];
  }
  return url;
}

// ── Security scan badge patterns ──
const SECURITY_BLOCKED_RE = /🛡️?\s*\*?\*?Security scan blocked tool "([^"]+)"\*?\*?(.*)/i;
const SECURITY_PASSED_RE = /🛡️?\s*(?:Skill\s+)?"?([^"]+?)"?\s+passed security scan/i;
const SECURITY_WARNING_RE = /🛡️?\s*\*?\*?Security warning for skill "([^"]+)"\*?\*?(.*)/i;
const VERIFICATION_WARNING_RE = /⚠️\s*(?:VERIFICATION|Low confidence\s*\(\d+%\)):\s*(.*)/i;
const TOOL_SCANNED_RE = /🛡️?\s*Tool "([^"]+)" scanned — (safe|clean|passed|ok)/i;

// ── Payment activity detection ──
const PAYMENT_ACTIVITY_RE = /\[PAYMENT_ACTIVITY:([^\]]*)\]/g;
const PAYMENT_KEYWORDS_RE = /\b(credit\s*card|debit\s*card|card\s*number|cvv|expir(?:y|ation)|billing|checkout|payment|pay\s*now|place\s*order|complete\s*purchase|add\s*to\s*cart.*checkout|card\s*details|cardholder)\b/gi;

function PaymentBadge({ detail }: { detail: string }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: 8,
      background: 'color-mix(in srgb, #f59e0b 12%, transparent)',
      border: '1px solid color-mix(in srgb, #f59e0b 30%, transparent)',
      color: '#f59e0b',
      fontSize: '0.72rem',
      fontWeight: 600,
      margin: '4px 0',
    }}>
      <CreditCardIcon size={13} color="#f59e0b" />
      <span>{detail || 'Payment activity detected'}</span>
    </div>
  );
}

// ── Internal tags to strip from display ──
const INTERNAL_TAG_PATTERNS = [
  /\[TOOL_START\].*?\[\/TOOL_START\]/gs,
  /\[\/TOOL_END\]/g,
  /\[TOOL_END\]/g,
  /\[TOOL_START\][^\n]*/g,
  /\[GROUND_TRUTH[^\]]*\][\s\S]*?\[\/GROUND_TRUTH[^\]]*\]/g,
  /\[ACTION_REPORT\][\s\S]*?\[\/ACTION_REPORT\]/g,
  /\[SCREENSHOT_ANALYSIS\][\s\S]*?\[\/SCREENSHOT_ANALYSIS\]/g,
  /\[FORM_FIELDS\][\s\S]*?\[\/FORM_FIELDS\]/g,
  /\[EXTENSION_STATUS\][^\n]*/g,
  /\[EXTENSION_ACTIONS:[^\]]*\]/g,
  /\[ACTIONS\][\s\S]*?\[\/ACTIONS\]/g,
  /\[DESKTOP_ACTIONS\][\s\S]*?\[\/DESKTOP_ACTIONS\]/g,
  /\[CLOUD_BROWSER_STATUS\][^\n]*/g,
  /<thinking>[\s\S]*?<\/thinking>/gi,
  /\[PIPELINE_STAGE:[^\]]*\][^\n]*/g,
  /\[CLOUD_BROWSER_REPORT\][\s\S]*?\[\/CLOUD_BROWSER_REPORT\]/g,
  /\[MEDIA_TOOL_MODE\][\s\S]*?\[\/MEDIA_TOOL_MODE\]/g,
  /\[TASK_TYPE_GUARD\][\s\S]*?\[\/TASK_TYPE_GUARD\]/g,
  /\[TOOL_STATUS:\s*(?:SUCCESS|FAILED)\]\s*/g,
  /\[MEMORY RULE\][\s\S]*?\[END MEMORY RULE\]/g,
  /\[FULL_AUTO_BROWSER\][^\n]*/g,
  /\[SMART_BROWSING_RULES\][\s\S]*?\[\/SMART_BROWSING_RULES\]/g,
  /\[CITATION_RULES\][\s\S]*?\[\/CITATION_RULES\]/g,
  /\[MEDIA_OUTPUT_RULES\][\s\S]*?\[\/MEDIA_OUTPUT_RULES\]/g,
  // Strip tool status emoji prefixes (⚡/❌ Tool: ...) — clean up for display
  /^[⚡❌]\s*Tool:\s*/gm,
  // Strip "Tool: xxx" lines entirely (with or without emoji)
  /^\s*(?:[⚡❌🔧✅]\s*)?Tool:\s+\S+.*$/gm,
  // Strip failed tool warning lines that shouldn't be shown to users
  /⚠️\s*THIS TOOL CALL FAILED[^\n]*/g,
  // Strip credential requirement markers
  /\[CRED_REQUIRED:[^\]]*\]/g,
  /\[SECRET_LABELS_NEEDED:[^\]]*\]/g,
  // Strip autonomous task status headers
  /\[(?:Autonomous Task|Manual Task Run)(?:\s+Failed)?\]\s*[^\n]*/g,
  // Strip internal error messages (Ollama, model errors, etc.)
  /\bOllama error \d+:[^\n]*/gi,
  /\bService temporarily unavailable[^\n]*/gi,
  // Strip payment activity markers (rendered as badges separately)
  /\[PAYMENT_ACTIVITY:[^\]]*\]/g,
  // Strip TOOL_SCAN/TOOL_EXEC markers
  /\[TOOL_SCAN:[^\]]*\]/g,
  /\[TOOL_EXEC:[^\]]*\]/g,
  // Strip TOKEN_USAGE markers
  /\[TOKEN_USAGE:\d+\]/g,
  // Strip tool result narration (credentials, secrets, etc.)
  /^.*No secrets (?:saved yet|currently saved).*$/gm,
  /^.*Use propose_secrets.*$/gm,
  /^.*Saved secret labels.*$/gm,
  /^.*Reference these in tool parameters.*$/gm,
  /^.*_label suffix fields.*$/gm,
  /^.*credential value\(s\).*$/gm,
];

/** Strip internal system tags and clean up raw artifacts */
function cleanContent(raw: string): string {
  let text = raw;
  for (const pat of INTERNAL_TAG_PATTERNS) {
    text = text.replace(pat, '');
  }
  // Strip runs of &nbsp; / non-breaking spaces used as vertical spacers
  text = text.replace(/(&nbsp;\s*){2,}/gi, ' ');
  text = text.replace(/(\u00a0\s*){2,}/g, ' ');
  // Strip lines that are only whitespace, &nbsp;, or non-breaking spaces
  text = text.replace(/^[ \t\u00a0]*(&nbsp;[ \t\u00a0]*)+$/gim, '');
  // Collapse 3+ consecutive newlines (including lines with only whitespace) into 2
  text = text.replace(/(\n\s*){3,}/g, '\n\n');
  // Collapse repeated blank lines between table rows/headers
  text = text.replace(/(\|[^\n]*\|)\n{2,}(\|)/g, '$1\n$2');
  // Remove blank lines inside markdown tables
  text = text.replace(/\n{2,}(?=\|[\s-]+\|)/g, '\n');
  return text.trim();
}

/** Convert markdown to clean plaintext for clipboard */
function markdownToPlaintext(md: string): string {
  let text = cleanContent(md);
  // Remove media tokens
  text = text.replace(MEDIA_TOKEN_REGEX, '');
  // Headers: ## Title → Title
  text = text.replace(/^#{1,6}\s+/gm, '');
  // Bold/italic
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
  text = text.replace(/\*\*(.+?)\*\*/g, '$1');
  text = text.replace(/\*(.+?)\*/g, '$1');
  text = text.replace(/__(.+?)__/g, '$1');
  text = text.replace(/_(.+?)_/g, '$1');
  // Strikethrough
  text = text.replace(/~~(.+?)~~/g, '$1');
  // Inline code
  text = text.replace(/`([^`]+)`/g, '$1');
  // Code blocks
  text = text.replace(/```[\s\S]*?```/g, (match) => {
    return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
  });
  // Links: [text](url) → text (url)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
  // Images: ![alt](url) → remove
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');
  // Checklists: - [x] → ✓, - [ ] → ○
  text = text.replace(/^(\s*)-\s*\[x\]\s*/gm, '$1✓ ');
  text = text.replace(/^(\s*)-\s*\[\s*\]\s*/gm, '$1○ ');
  // Bullet points: - item → • item
  text = text.replace(/^(\s*)-\s+/gm, '$1• ');
  text = text.replace(/^(\s*)\*\s+/gm, '$1• ');
  // Blockquotes
  text = text.replace(/^>\s*/gm, '');
  // Horizontal rules
  text = text.replace(/^---+$/gm, '─────────────────');
  // Tables: | col | col | → col    col
  text = text.replace(/^\|?-+(\|-+)*\|?$/gm, ''); // separator rows
  text = text.replace(/\|/g, '\t');
  // Security scan emoji lines — keep as-is, they're informational
  // Collapse whitespace
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

const ShieldIcon = ({ color = 'currentColor' }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const ShieldAlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="M12 8v4" /><path d="M12 16h.01" />
  </svg>
);

const ShieldXIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m14.5 9.5-5 5" /><path d="m9.5 9.5 5 5" />
  </svg>
);

const CreditCardIcon = ({ size = 14, color = '#f59e0b' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function SecurityBadge({ type, toolName, details }: { type: 'safe' | 'blocked' | 'warning'; toolName: string; details?: string }) {
  const className = type === 'safe'
    ? styles.securityBadgeSafe
    : type === 'blocked'
      ? styles.securityBadgeBlocked
      : styles.securityBadgeWarning;
  const Icon = type === 'safe' ? ShieldIcon : type === 'blocked' ? ShieldXIcon : ShieldAlertIcon;
  const label = type === 'safe' ? 'Scanned — Safe' : type === 'blocked' ? 'BLOCKED — Denied' : 'Warning';

  return (
    <div className={className}>
      <Icon />
      <span>{toolName}</span>
      <span style={{ opacity: 0.8, fontWeight: 400 }}>·</span>
      <span>{label}</span>
      {details && <span className={styles.securityBadgeDetails}>— {details.replace(/\*\*/g, '').trim()}</span>}
    </div>
  );
}

function parseSecurityLine(line: string): { type: 'safe' | 'blocked' | 'warning'; toolName: string; details?: string } | null {
  let m: RegExpMatchArray | null;
  if ((m = line.match(SECURITY_BLOCKED_RE))) return { type: 'blocked', toolName: m[1], details: m[2] };
  if ((m = line.match(SECURITY_WARNING_RE))) return { type: 'warning', toolName: m[1], details: m[2] };
  if ((m = line.match(SECURITY_PASSED_RE))) return { type: 'safe', toolName: m[1] };
  if ((m = line.match(TOOL_SCANNED_RE))) return { type: 'safe', toolName: m[1] };
  if ((m = line.match(VERIFICATION_WARNING_RE))) return { type: 'warning', toolName: 'Verification', details: m[1] };
  return null;
}

/** Convert inline [1], [2] etc. citation refs to styled HTML spans (rehypeRaw handles them) */
function injectCitationBadges(text: string): string {
  // Only transform [N] that appear in running text, not inside markdown link syntax [text](url)
  return text.replace(/(?<!\[[^\]]*)\[(\d{1,3})\](?!\()/g, (_, num) =>
    `<span class="${styles.citationRef}">${num}</span>`
  );
}

function renderMarkdown(content: string) {
  const processed = injectCitationBadges(content);
  return (
    <ReactMarkdown
      children={processed}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, rehypeHighlight]}
      components={{
        h1: ({node, ...props}) => <h1 className={styles.mdHeading} {...props} />,
        h2: ({node, ...props}) => <h2 className={styles.mdHeading} {...props} />,
        h3: ({node, ...props}) => <h3 className={styles.mdHeading} {...props} />,
        h4: ({node, ...props}) => <h4 className={styles.mdHeading} {...props} />,
        h5: ({node, ...props}) => <h5 className={styles.mdHeading} {...props} />,
        h6: ({node, ...props}) => <h6 className={styles.mdHeading} {...props} />,
        p: ({node, children, ...props}) => {
          // Skip empty paragraphs to avoid excessive vertical spacing
          const isEmpty = !children || (Array.isArray(children) && children.every((c: any) => c === '\n' || c === '' || c == null));
          if (isEmpty) return null;
          return <p className={styles.mdParagraph} {...props}>{children}</p>;
        },
        ul: ({node, className, ...props}: any) => {
          const isTaskList = className === 'contains-task-list' ||
            (typeof className === 'string' && className.includes('task-list'));
          return (
            <ul
              className={isTaskList ? styles.mdTaskList : styles.mdList}
              style={isTaskList ? undefined : { listStyleType: 'disc' }}
              {...props}
            />
          );
        },
        ol: ({node, ...props}) => <ol className={styles.mdList} style={{ listStyleType: 'decimal' }} {...props} />,
        li: ({node, checked, className, children, ...props}: any) => {
          if (typeof checked === 'boolean') {
            return (
              <li className={styles.mdTaskItem} {...props}>
                <span className={checked ? styles.mdCheckboxChecked : styles.mdCheckbox}>
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.5L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className={checked ? styles.mdTaskTextDone : undefined}>{children}</span>
              </li>
            );
          }
          return <li {...props}>{children}</li>;
        },
        blockquote: ({node, ...props}) => <blockquote className={styles.mdBlockquote} {...props} />,
        code({className, children, ...props}: any) {
          const isInline = !className;
          return isInline ? (
            <code className={styles.inlineCode} {...props}>{children}</code>
          ) : (
            <pre className={styles.mdCodeBlock}><code className={className} {...props}>{children}</code></pre>
          );
        },
        table: ({node, ...props}) => <div className={styles.mdTableWrap}><table className={styles.mdTable} {...props} /></div>,
        thead: ({node, ...props}) => <thead {...props} />,
        tbody: ({node, ...props}) => <tbody {...props} />,
        tr: ({node, ...props}) => <tr {...props} />,
        th: ({node, ...props}) => <th {...props} />,
        td: ({node, ...props}) => <td {...props} />,
        a: ({node, ...props}) => (
          <a className={styles.inlineLink} target="_blank" rel="noopener noreferrer" {...props} />
        ),
        img: ({node, ...props}) => <img style={{ maxWidth: '100%', borderRadius: 'var(--radius, 8px)' }} loading="lazy" crossOrigin="anonymous" {...props} />,
        hr: ({node, ...props}) => <hr className={styles.mdHr} {...props} />,
        input: ({node, type, checked, ...props}: any) => {
          if (type === 'checkbox') {
            return (
              <span className={checked ? styles.mdCheckboxChecked : styles.mdCheckbox}>
                {checked && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.5L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
            );
          }
          return <input type={type} checked={checked} {...props} />;
        },
      }}
    />
  );
}

function parseMediaSegments(content: string): MediaSegment[] {
  const segments: MediaSegment[] = [];
  const seenUrls = new Set<string>(); // Deduplicate identical media URLs
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = MEDIA_TOKEN_REGEX.exec(content)) !== null) {
    const [fullMatch, mediaType, mediaUrl] = match;
    const url = cleanMediaUrl(mediaUrl.trim());
    const start = match.index;

    // Skip obviously truncated/invalid URLs (e.g. streaming partial like "https://task")
    // A valid URL must have a domain with a TLD (dot) or be a relative/data URL
    if (url.startsWith('http') && !url.includes('.') && !url.includes('localhost')) {
      continue;
    }
    // Skip R2 URLs missing file extension (truncated during streaming or by model)
    if (url.includes('/_api/r2/') && !/\.\w{2,5}(?:\?|$)/.test(url)) {
      continue;
    }

    if (start > lastIndex) {
      const markdown = content.slice(lastIndex, start).trim();
      if (markdown) segments.push({ type: 'markdown', content: markdown });
    }
    // Skip duplicate media URLs — only show each image/video once
    if (!seenUrls.has(url)) {
      seenUrls.add(url);
      segments.push({
        type: mediaType.toLowerCase() as MediaSegment['type'],
        url,
      });
    }
    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < content.length) {
    const markdown = content.slice(lastIndex).trim();
    if (markdown) segments.push({ type: 'markdown', content: markdown });
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', content }];
}

// ─── Inline PDF Viewer with zoom + two-page spread ──
function buildPdfViewerSrcDoc(pdfUrl: string): string {
  const safeUrl = pdfUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.min.mjs" type="module"></script>
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:100%;height:100%;background:#111;font-family:Inter,system-ui,sans-serif;color:#fff;overflow:hidden}
#viewer{display:flex;flex-direction:column;height:100%}
#canvasWrap{flex:1;display:flex;align-items:center;justify-content:center;overflow:auto;position:relative;gap:2px}
canvas{display:block;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,.4)}
#controls{display:flex;align-items:center;justify-content:center;padding:6px 12px;background:#1a1a1a;border-top:1px solid #2a2a2a;gap:6px;flex-shrink:0;flex-wrap:wrap}
#controls button{background:#222;border:1px solid #333;color:#ccc;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;font-weight:600;display:flex;align-items:center;gap:3px}
#controls button:hover{background:#2a2a2a;border-color:#444}
#controls button:disabled{opacity:.3;cursor:default}
#controls button:disabled:hover{background:#222;border-color:#333}
#controls button.active{background:#f97316;border-color:#f97316;color:#fff}
.sep{width:1px;height:18px;background:#333;flex-shrink:0}
#pageInfo{font-size:11px;color:#888;white-space:nowrap}
#zoomInfo{font-size:10px;color:#666;white-space:nowrap}
#loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#666;font-size:13px}
.arrow{font-size:13px}
</style>
</head>
<body>
<div id="viewer">
<div id="canvasWrap">
<div id="loading">Loading PDF...</div>
<canvas id="c1"></canvas>
<canvas id="c2" style="display:none"></canvas>
</div>
<div id="controls">
<button id="prev" disabled><span class="arrow">&#9664;</span></button>
<span id="pageInfo">—</span>
<button id="next" disabled><span class="arrow">&#9654;</span></button>
<div class="sep"></div>
<button id="zoomOut" title="Zoom out">−</button>
<span id="zoomInfo">Fit</span>
<button id="zoomIn" title="Zoom in">+</button>
<button id="zoomFit" title="Fit to page">Fit</button>
<div class="sep"></div>
<button id="spreadBtn" title="Toggle two-page spread">&#9776; Spread</button>
</div>
</div>
<script type="module">
import{GlobalWorkerOptions,getDocument}from"https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.min.mjs";
GlobalWorkerOptions.workerSrc="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.worker.min.mjs";
const url="${safeUrl}";
const c1=document.getElementById("c1"),c2=document.getElementById("c2");
const ctx1=c1.getContext("2d"),ctx2=c2.getContext("2d");
const info=document.getElementById("pageInfo");
const zoomInfo=document.getElementById("zoomInfo");
const prevBtn=document.getElementById("prev"),nextBtn=document.getElementById("next");
const zoomInBtn=document.getElementById("zoomIn"),zoomOutBtn=document.getElementById("zoomOut"),zoomFitBtn=document.getElementById("zoomFit");
const spreadBtn=document.getElementById("spreadBtn");
const loadEl=document.getElementById("loading");
let pdf=null,pg=1,rendering=false,pendingRender=false,userZoom=0,spread=false;
const ZOOM_STEPS=[0.5,0.75,1,1.25,1.5,2,2.5,3];
function fitScale(page,w,h){
const vp1=page.getViewport({scale:1});
return Math.min((w)/vp1.width,(h)/vp1.height);
}
async function renderPage(canvas,ctx,pageNum,maxW,maxH){
const page=await pdf.getPage(pageNum);
let scale;
if(userZoom>0){scale=userZoom;}
else{scale=fitScale(page,Math.max(maxW,50),Math.max(maxH,50));}
const vp=page.getViewport({scale});
canvas.width=vp.width;canvas.height=vp.height;
await page.render({canvasContext:ctx,viewport:vp}).promise;
}
async function render(){
if(rendering){pendingRender=true;return;}
if(!pdf)return;
rendering=true;pendingRender=false;
const wrap=document.getElementById("canvasWrap");
const ww=Math.max(wrap.clientWidth-16,100),wh=Math.max(wrap.clientHeight-16,100);
c1.style.display="block";
try{
if(spread&&pdf.numPages>1){
const halfW=Math.floor(ww/2)-4;
c2.style.display="block";
await renderPage(c1,ctx1,pg,halfW,wh);
const pg2=pg+1<=pdf.numPages?pg+1:null;
if(pg2){await renderPage(c2,ctx2,pg2,halfW,wh);}
else{c2.style.display="none";}
const endPg=pg2||pg;
info.textContent=pg+(pg2?" - "+pg2:"")+" of "+pdf.numPages;
prevBtn.disabled=pg<=1;
nextBtn.disabled=endPg>=pdf.numPages;
}else{
c2.style.display="none";
await renderPage(c1,ctx1,pg,ww,wh);
info.textContent=pg+" of "+pdf.numPages;
prevBtn.disabled=pg<=1;nextBtn.disabled=pg>=pdf.numPages;
}
}catch(renderErr){console.warn("PDF render error:",renderErr);}
zoomInfo.textContent=userZoom>0?Math.round(userZoom*100)+"%":"Fit";
rendering=false;
if(pendingRender){pendingRender=false;requestAnimationFrame(()=>render());}
}
function step(dir){
if(spread){const s=dir>0?2:-2;const np=pg+s;if(np>=1&&np<=pdf.numPages)pg=np;else if(dir>0&&pg+1<pdf.numPages)pg=pdf.numPages;}
else{pg+=dir;}
pg=Math.max(1,Math.min(pg,pdf.numPages));
render();
}
prevBtn.onclick=()=>step(-1);
nextBtn.onclick=()=>step(1);
zoomInBtn.onclick=()=>{
const cur=userZoom||1;
const next=ZOOM_STEPS.find(z=>z>cur+0.01);
userZoom=next||ZOOM_STEPS[ZOOM_STEPS.length-1];
render();
};
zoomOutBtn.onclick=()=>{
const cur=userZoom||1;
const next=[...ZOOM_STEPS].reverse().find(z=>z<cur-0.01);
userZoom=next||ZOOM_STEPS[0];
render();
};
zoomFitBtn.onclick=()=>{userZoom=0;render()};
spreadBtn.onclick=()=>{
spread=!spread;
spreadBtn.classList.toggle("active",spread);
if(spread&&pg%2===0)pg=Math.max(1,pg-1);
rendering=false;pendingRender=false;
requestAnimationFrame(()=>render());
};
document.addEventListener("keydown",e=>{
if(e.key==="ArrowLeft")step(-1);
if(e.key==="ArrowRight")step(1);
if(e.key==="+"||e.key==="=")zoomInBtn.click();
if(e.key==="-")zoomOutBtn.click();
if(e.key==="0")zoomFitBtn.click();
});
async function loadPdf(src){
try{
pdf=await getDocument(src).promise;
loadEl.style.display="none";
render();
new ResizeObserver(()=>{if(pdf&&!rendering)render();}).observe(document.getElementById("canvasWrap"));
}catch(e){loadEl.textContent="Failed to load PDF: "+e.message;console.error("PDF load error:",e)}
}
// Listen for PDF data from parent window (pre-fetched with auth cookies)
window.addEventListener("message",async(ev)=>{
if(ev.data&&ev.data.type==="pdf-data"&&ev.data.dataUrl){
try{
const resp=await fetch(ev.data.dataUrl);
const buf=await resp.arrayBuffer();
loadPdf({data:buf});
}catch(e){console.error("PDF data load error:",e);loadPdf({url,withCredentials:true})}
}
});
// Also try direct URL fetch as fallback (works when same-origin cookies are available)
setTimeout(()=>{if(!pdf)loadPdf({url,withCredentials:true});},2000);
</script>
</body></html>`;
}

interface WebhookTarget {
  id: string;
  name: string;
  webhookUrl?: string;
  webhook_url?: string;
}

export function InlinePdfViewer({ url }: { url: string }) {
  const fallbackName = url.split('/').pop()?.split('?')[0] || 'document.pdf';
  const [fileName, setFileName] = useState(fallbackName);
  const [showEditor, setShowEditor] = useState(false);
  const [fileStatus, setFileStatus] = useState<'checking' | 'ok' | 'missing'>('checking');
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch PDF from parent window (has auth cookies), convert to data URL,
  // then pass to the iframe. This avoids CORS/CSP issues with pdf.js loading
  // from CDN inside sandboxed srcDoc iframes.
  useEffect(() => {
    setFileStatus('checking');
    setPdfDataUrl(null);
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) { setFileStatus('missing'); return; }
        setFileStatus('ok');
        const cd = res.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="([^"]+)"/);
        if (match?.[1]) setFileName(match[1]);
        // Convert to base64 data URL for the iframe
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onload = () => {
          setPdfDataUrl(reader.result as string);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => { setFileStatus('missing'); });
  }, [url, retryCount]);

  // Post PDF data to iframe once it's ready
  useEffect(() => {
    if (!pdfDataUrl || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const sendData = () => {
      iframe.contentWindow?.postMessage({ type: 'pdf-data', dataUrl: pdfDataUrl }, '*');
    };
    // iframe might not be ready yet
    iframe.addEventListener('load', sendData);
    // Also try immediately in case iframe already loaded
    sendData();
    return () => iframe.removeEventListener('load', sendData);
  }, [pdfDataUrl]);
  const [PdfEditorComp, setPdfEditorComp] = useState<React.ComponentType<any> | null>(null);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [docxLoading, setDocxLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTargets, setShareTargets] = useState<WebhookTarget[] | null>(null);
  const [shareLoading, setShareLoading] = useState<string | null>(null);
  const [shareResult, setShareResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const shareRef = useRef<HTMLDivElement>(null);

  const handleEdit = useCallback(async () => {
    try {
      if (!PdfEditorComp) {
        const mod = await import('../components/PdfEditor');
        if (!mod?.PdfEditor) {
          console.error('[PDF Edit] PdfEditor component not found in module', mod);
          alert('PDF editor failed to load — module is missing the PdfEditor export.');
          return;
        }
        setPdfEditorComp(() => mod.PdfEditor);
      }
      setShowEditor(true);
    } catch (err) {
      const msg = (err as Error)?.message || String(err);
      console.error('[PDF Edit] Failed to load editor:', err);
      // Stale chunk after a deploy — hard reload to fetch the new bundle
      if (msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('error loading dynamically imported module') ||
          msg.includes('ChunkLoadError')) {
        window.location.reload();
        return;
      }
      alert(`Failed to load PDF editor: ${msg}. Check console for details.`);
    }
  }, [PdfEditorComp]);

  const handleDocxDownload = useCallback(async () => {
    setDocxLoading(true);
    try {
      const res = await fetch('/_api/taskingbot/pdf-to-docx', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: currentUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await res.blob();
      const docxName = fileName.replace(/\.pdf$/i, '.docx');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = docxName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      console.error('[DOCX download]', e);
    } finally {
      setDocxLoading(false);
    }
  }, [currentUrl, fileName]);

  // Load webhook targets when share dropdown opens
  const handleShareToggle = useCallback(async () => {
    if (shareOpen) {
      setShareOpen(false);
      return;
    }
    setShareOpen(true);
    setShareResult(null);
    if (shareTargets === null) {
      try {
        const res = await fetch('/_api/taskingbot/webhooks', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setShareTargets(Array.isArray(data) ? data : []);
        } else {
          setShareTargets([]);
        }
      } catch {
        setShareTargets([]);
      }
    }
  }, [shareOpen, shareTargets]);

  const handleShareTo = useCallback(async (webhook: WebhookTarget, format: 'pdf' | 'docx') => {
    setShareLoading(`${webhook.id}-${format}`);
    setShareResult(null);
    try {
      const res = await fetch('/_api/taskingbot/share-document', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentUrl: currentUrl,
          format,
          webhookId: webhook.id,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const targetLabel = data.target === 'teams' ? 'Teams' : data.target === 'slack' ? 'Slack' : webhook.name;
        setShareResult({ ok: true, msg: `Sent to ${targetLabel}` });
      } else {
        setShareResult({ ok: false, msg: data.error || 'Share failed' });
      }
    } catch {
      setShareResult({ ok: false, msg: 'Share failed' });
    } finally {
      setShareLoading(null);
    }
  }, [currentUrl]);

  // Close share dropdown on outside click
  useEffect(() => {
    if (!shareOpen) return;
    const handler = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShareOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [shareOpen]);

  if (fileStatus === 'missing') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
        background: 'color-mix(in srgb, var(--foreground) 8%, transparent)',
        fontSize: '0.85rem',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <span style={{ opacity: 0.5 }}>{fallbackName} — file not available</span>
        <button
          onClick={() => setRetryCount((c) => c + 1)}
          style={{
            marginLeft: 'auto', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius)',
            border: '1px solid color-mix(in srgb, var(--foreground) 20%, transparent)',
            background: 'transparent', cursor: 'pointer', fontSize: '0.75rem',
            color: 'inherit', opacity: 0.7,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (fileStatus === 'checking') {
    return (
      <div className={styles.pdfViewerCard} style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>Loading PDF...</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.pdfViewerCard}>
        <div className={styles.pdfViewerWrap}>
          <iframe
            ref={iframeRef}
            srcDoc={buildPdfViewerSrcDoc(currentUrl)}
            className={styles.pdfIframe}
            title={`PDF: ${fileName}`}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
        <div className={styles.pdfViewerFooter}>
          <div className={styles.pdfViewerTitle}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span>{fileName}</span>
          </div>
          <div className={styles.pdfViewerActions}>
            <button onClick={handleEdit} className={styles.pdfViewerOpenBtn} title="Edit PDF">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
              Edit
            </button>
            <a href={currentUrl} target="_blank" rel="noopener noreferrer" className={styles.pdfViewerOpenBtn}>
              Open
            </a>
            <a href={currentUrl} download={fileName} className={styles.pdfViewerDownloadBtn} title="Download PDF">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </a>
            <button onClick={handleDocxDownload} disabled={docxLoading} className={styles.pdfViewerOpenBtn} title="Download as DOCX" style={{ gap: '4px' }}>
              <WordIcon size={13} />
              {docxLoading ? '...' : 'DOCX'}
            </button>
            {/* Share to Teams/Slack */}
            <div className={styles.shareDropdownWrap} ref={shareRef}>
              <button onClick={handleShareToggle} className={styles.pdfViewerOpenBtn} title="Share to Teams or Slack">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                Share
              </button>
              {shareOpen && (
                <div className={styles.shareDropdown}>
                  {/* Always show Copy Link + Share Link options */}
                  <div
                    className={styles.shareDropdownItem}
                    style={{ cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        const pdfUrlForShare = pdfUrl || '';
                        if (pdfUrlForShare) {
                          // Generate a share link via the API
                          const res = await fetch('/_api/user-media/share-link', {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              action: 'create',
                              assetType: 'media',
                              assetId: pdfUrlForShare.split('/').pop()?.split('.')[0] || 'pdf',
                              permission: 'download',
                            }),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            if (data.shareUrl) {
                              await navigator.clipboard.writeText(data.shareUrl);
                              setShareResult({ ok: true, msg: 'Share link copied!' });
                              setTimeout(() => setShareResult(null), 2000);
                              return;
                            }
                          }
                        }
                        // Fallback: copy the direct URL
                        const fullUrl = pdfUrlForShare.startsWith('http') ? pdfUrlForShare : `https://tasking.tech${pdfUrlForShare}`;
                        await navigator.clipboard.writeText(fullUrl);
                        setShareResult({ ok: true, msg: 'Link copied!' });
                        setTimeout(() => setShareResult(null), 2000);
                      } catch {
                        setShareResult({ ok: false, msg: 'Failed to copy' });
                      }
                    }}
                  >
                    📋 Copy Share Link
                  </div>
                  <div style={{ height: 1, background: 'var(--border, #333)', margin: '4px 0' }} />
                  {shareTargets === null ? (
                    <div className={styles.shareDropdownItem} style={{ justifyContent: 'center' }}>Loading webhooks...</div>
                  ) : shareTargets.length === 0 ? (
                    <div className={styles.shareDropdownEmpty} style={{ fontSize: '0.7rem', padding: '6px 10px' }}>
                      No webhook channels configured.
                    </div>
                  ) : (
                    <>
                      {shareResult && (
                        <div className={shareResult.ok ? styles.shareSuccess : styles.shareError}>
                          {shareResult.msg}
                        </div>
                      )}
                      {shareTargets.map((wh) => {
                        const whUrl = wh.webhookUrl || wh.webhook_url || '';
                        const isTeams = whUrl.includes('webhook.office.com') || whUrl.includes('workflows.office.com') || whUrl.includes('.logic.azure.com');
                        const isSlack = whUrl.includes('hooks.slack.com');
                        const icon = isTeams ? '🟦' : isSlack ? '🟪' : '🔗';
                        return (
                          <div key={wh.id} className={styles.shareDropdownItem}>
                            <span className={styles.shareTargetName}>{icon} {wh.name}</span>
                            <div className={styles.shareTargetActions}>
                              <button
                                className={styles.shareTargetBtn}
                                disabled={shareLoading === `${wh.id}-pdf`}
                                onClick={() => handleShareTo(wh, 'pdf')}
                              >
                                {shareLoading === `${wh.id}-pdf` ? '...' : 'PDF'}
                              </button>
                              <button
                                className={styles.shareTargetBtn}
                                disabled={shareLoading === `${wh.id}-docx`}
                                onClick={() => handleShareTo(wh, 'docx')}
                              >
                                {shareLoading === `${wh.id}-docx` ? '...' : 'DOCX'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showEditor && PdfEditorComp && createPortal(
        <PdfEditorComp
          pdfUrl={currentUrl}
          onClose={() => setShowEditor(false)}
          onSaved={(newUrl: string) => setCurrentUrl(newUrl)}
        />,
        document.body,
      )}
    </>
  );
}

function InlinePptxViewer({ url }: { url: string }) {
  const fileName = url.split('/').pop()?.split('?')[0] || 'presentation.pptx';

  return (
    <div className={styles.pdfViewerCard}>
      <div
        className={styles.pdfViewerWrap}
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          position: 'relative',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#fff',
          }}
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f97316"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ margin: '0 auto 1rem' }}
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '0.5rem' }}>
            {fileName.replace(/\.pptx?$/i, '')}
          </div>
          <div style={{ fontSize: '11px', color: '#888' }}>PowerPoint Presentation</div>
        </div>
      </div>
      <div className={styles.pdfViewerFooter}>
        <div className={styles.pdfViewerTitle}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          <span>{fileName}</span>
        </div>
        <div className={styles.pdfViewerActions}>
          <a href={url} target="_blank" rel="noopener noreferrer" className={styles.pdfViewerOpenBtn}>
            Open
          </a>
          <a href={url} download={fileName} className={styles.pdfViewerDownloadBtn}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

function buildGlbViewerSrcDoc(url: string): string {
  const safeUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle at top, #1f2937 0%, #0b1120 58%, #020617 100%);
        overflow: hidden;
        font-family: Inter, system-ui, sans-serif;
      }
      model-viewer {
        width: 100%;
        height: 100%;
        --poster-color: transparent;
      }
      .err {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: rgba(255,255,255,0.5);
        font-size: 13px;
        text-align: center;
        padding: 20px;
      }
      .label {
        position: absolute;
        left: 12px;
        bottom: 10px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.72);
        color: rgba(255,255,255,0.84);
        font-size: 12px;
        letter-spacing: 0.02em;
        backdrop-filter: blur(10px);
        z-index: 2;
      }
    </style>
  </head>
  <body>
    <div id="viewer-root"></div>
    <div class="label">Drag to rotate. Scroll to zoom.</div>
    <script type="module">
      import('https://unpkg.com/@google/model-viewer@3.5.0/dist/model-viewer.min.js')
        .then(() => {
          const mv = document.createElement('model-viewer');
          mv.setAttribute('src', ${JSON.stringify(url)});
          mv.setAttribute('camera-controls', '');
          mv.setAttribute('touch-action', 'pan-y');
          mv.setAttribute('autoplay', '');
          mv.setAttribute('shadow-intensity', '1');
          mv.setAttribute('exposure', '1');
          mv.setAttribute('environment-image', 'neutral');
          mv.setAttribute('ar-status', 'not-presenting');
          mv.style.cssText = 'width:100%;height:100%';
          document.getElementById('viewer-root').replaceWith(mv);
        })
        .catch((e) => {
          document.getElementById('viewer-root').innerHTML =
            '<div class="err">Failed to load 3D viewer.<br>Click "Open .glb" below to download.</div>';
          console.error('model-viewer load error:', e);
        });
    </script>
  </body>
</html>`;
}

/* ── SVG icon helpers ── */
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const ZoomInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const EraserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" /><path d="M22 21H7" /><path d="m5 11 9 9" />
  </svg>
);
const SparklesIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);
const ZoomOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);
const BoxSelectIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3a2 2 0 0 0-2 2" /><path d="M19 3a2 2 0 0 1 2 2" /><path d="M21 19a2 2 0 0 1-2 2" /><path d="M5 21a2 2 0 0 1-2-2" />
    <path d="M9 3h1" /><path d="M9 21h1" /><path d="M14 3h1" /><path d="M14 21h1" /><path d="M3 9v1" /><path d="M21 9v1" /><path d="M3 14v1" /><path d="M21 14v1" />
  </svg>
);
const LassoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 22a5 5 0 0 1-2-4" /><path d="M3.3 14A6.8 6.8 0 0 1 2 10c0-4.4 4.5-8 10-8s10 3.6 10 8-4.5 8-10 8a12 12 0 0 1-3.7-.5" />
  </svg>
);

/* ── Copy Button ── */
function CopyButton({ rawContent }: { rawContent: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      const plainText = markdownToPlaintext(rawContent);
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = markdownToPlaintext(rawContent);
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [rawContent]);

  return (
    <button
      className={styles.msgCopyBtn}
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy as clean text'}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

/* ── Region Edit (Inpaint) Modal ── */
function RegionEditModal({
  url,
  onClose,
  onSubmit,
}: {
  url: string;
  onClose: () => void;
  onSubmit: (text: string) => void;
}) {
  const [tool, setTool] = useState<'box' | 'lasso'>('box');
  const [prompt, setPrompt] = useState('');
  const [boxRect, setBoxRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [lassoPoints, setLassoPoints] = useState<{ x: number; y: number }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const overlayRef = useRef<SVGSVGElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const getRelativePos = useCallback((e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    const svg = overlayRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const handlePointerDown = useCallback((e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if ('touches' in e) e.preventDefault();
    const pos = getRelativePos(e);
    setIsDrawing(true);
    if (tool === 'box') {
      startRef.current = pos;
      setBoxRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
      setLassoPoints([]);
    } else {
      setLassoPoints([pos]);
      setBoxRect(null);
    }
  }, [tool, getRelativePos]);

  const handlePointerMove = useCallback((e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if (!isDrawing) return;
    if ('touches' in e) e.preventDefault();
    const pos = getRelativePos(e);
    if (tool === 'box' && startRef.current) {
      setBoxRect({
        x: Math.min(startRef.current.x, pos.x),
        y: Math.min(startRef.current.y, pos.y),
        w: Math.abs(pos.x - startRef.current.x),
        h: Math.abs(pos.y - startRef.current.y),
      });
    } else if (tool === 'lasso') {
      setLassoPoints((prev) => [...prev, pos]);
    }
  }, [isDrawing, tool, getRelativePos]);

  const handlePointerUp = useCallback(() => {
    setIsDrawing(false);
    startRef.current = null;
  }, []);

  const hasSelection = (tool === 'box' && boxRect && boxRect.w > 1 && boxRect.h > 1) ||
    (tool === 'lasso' && lassoPoints.length > 5);

  const handleSubmit = useCallback(() => {
    if (!prompt.trim()) return;
    const regionDesc = tool === 'box' && boxRect
      ? `region: box at ${Math.round(boxRect.x)}%,${Math.round(boxRect.y)}% size ${Math.round(boxRect.w)}%x${Math.round(boxRect.h)}%`
      : `region: freeform lasso selection`;
    onSubmit(`Edit the image at ${url}. ${regionDesc}. Change the selected area to: ${prompt.trim()}`);
    onClose();
  }, [prompt, tool, boxRect, url, onSubmit, onClose]);

  return (
    <div className={styles.regionEditBackdrop} onClick={onClose}>
      <div className={styles.regionEditModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.regionEditHeader}>
          <span className={styles.regionEditTitle}>Edit Region</span>
          <button className={styles.mediaModalBtn} onClick={onClose}><XIcon /></button>
        </div>
        <div className={styles.regionEditCanvasWrap}>
          <img src={url} alt="" className={styles.regionEditImage} draggable={false} />
          <div className={styles.regionEditOverlay}>
            <svg
              ref={overlayRef}
              className={styles.regionEditSvg}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={handlePointerUp}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
              onTouchCancel={handlePointerUp}
            >
              {boxRect && tool === 'box' && (
                <rect
                  x={boxRect.x} y={boxRect.y}
                  width={boxRect.w} height={boxRect.h}
                  className={styles.regionEditRect}
                />
              )}
              {lassoPoints.length > 1 && tool === 'lasso' && (
                <polyline
                  points={lassoPoints.map((p) => `${p.x},${p.y}`).join(' ')}
                  className={styles.regionEditLasso}
                />
              )}
            </svg>
          </div>
        </div>
        <div className={styles.regionEditToolbar}>
          <button
            className={`${styles.regionToolBtn} ${tool === 'box' ? styles.regionToolBtnActive : ''}`}
            onClick={() => { setTool('box'); setLassoPoints([]); }}
          >
            <BoxSelectIcon /> Box
          </button>
          <button
            className={`${styles.regionToolBtn} ${tool === 'lasso' ? styles.regionToolBtnActive : ''}`}
            onClick={() => { setTool('lasso'); setBoxRect(null); }}
          >
            <LassoIcon /> Lasso
          </button>
          <button
            className={styles.regionToolBtn}
            onClick={() => { setBoxRect(null); setLassoPoints([]); }}
          >
            Clear
          </button>
        </div>
        <textarea
          className={styles.regionEditPrompt}
          placeholder="Describe what to change in the selected area..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && hasSelection) { e.preventDefault(); handleSubmit(); } }}
        />
        <div className={styles.regionEditActions}>
          <button className={styles.regionActionBtn} onClick={onClose}>Cancel</button>
          <button
            className={`${styles.regionActionBtn} ${styles.regionActionPrimary}`}
            disabled={!hasSelection || !prompt.trim()}
            onClick={handleSubmit}
          >
            Inpaint
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Image card with hover actions ── */
function ImageCard({
  url,
  index,
  onOpenModal,
  onOpenInpaint,
  onRegenerate,
  onImageEnhance,
  onRemoveBackground,
}: {
  url: string;
  index: number;
  onOpenModal: (url: string) => void;
  onOpenInpaint: (url: string) => void;
  onRegenerate?: (text: string) => void;
  onImageEnhance?: (url: string) => void;
  onRemoveBackground?: (url: string) => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const retryCount = useRef(0);

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || 'image.png';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [url]);

  const handleOpen = useCallback(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  return (
    <div className={styles.mediaInlineWrap}>
      {imgFailed ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
            background: 'color-mix(in srgb, var(--foreground) 8%, transparent)',
            fontSize: '0.85rem', cursor: 'pointer',
          }}
          onClick={() => { setImgFailed(false); retryCount.current = 0; }}
        >
          <span style={{ opacity: 0.5 }}>Image failed to load.</span>
          <a href={url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--accent, #f97316)', textDecoration: 'underline' }}
          >Open directly</a>
          <span style={{ opacity: 0.5 }}>|</span>
          <span style={{ color: 'var(--accent, #f97316)' }}>Retry</span>
        </div>
      ) : (
        <img
          src={url}
          alt=""
          className={styles.inlineMediaImg}
          loading="lazy"
          crossOrigin={url.includes('/_api/r2/') ? undefined : 'anonymous'}
          onClick={() => onOpenModal(url)}
          onError={(e) => {
            // R2 URLs: fail immediately — if 404, the object doesn't exist
            if (url.includes('/_api/r2/')) {
              setImgFailed(true);
              return;
            }
            // External URLs: retry with cache-bust
            if (retryCount.current < 2) {
              retryCount.current++;
              const sep = url.includes('?') ? '&' : '?';
              e.currentTarget.src = `${url}${sep}_r=${Date.now()}`;
              return;
            }
            setImgFailed(true);
          }}
          style={{ cursor: 'zoom-in', margin: 0 }}
        />
      )}
      <div className={styles.mediaInlineActions}>
        <button className={styles.mediaInlineBtn} onClick={() => onOpenModal(url)} title="Zoom">
          <ZoomInIcon />
        </button>
        <button className={styles.mediaInlineBtn} onClick={handleDownload} title="Download">
          <DownloadIcon />
        </button>
        <button className={styles.mediaInlineBtn} onClick={handleOpen} title="Open in new tab">
          <ExternalLinkIcon />
        </button>
        <button className={styles.mediaInlineBtn} onClick={() => onOpenInpaint(url)} title="Edit region (inpaint)">
          <PencilIcon />
        </button>
        {onImageEnhance && (
          <button className={styles.mediaInlineBtn} onClick={() => onImageEnhance(url)} title="Enhance (2x upscale)">
            <SparklesIcon />
          </button>
        )}
        {onRemoveBackground && (
          <button type="button" className={styles.mediaInlineBtn} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveBackground(url); }} title="Remove background">
            <EraserIcon />
          </button>
        )}
        <button
          className={styles.mediaInlineBtn}
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('Remove this image?')) {
              // Try to delete from R2 if it's an internal URL
              if (url.includes('/_api/r2/')) {
                fetch(url, { method: 'DELETE', credentials: 'include' }).catch(() => {});
              }
              // Hide the card
              const wrap = (e.target as HTMLElement).closest(`.${styles.mediaInlineWrap}`);
              if (wrap) (wrap as HTMLElement).style.display = 'none';
            }
          }}
          title="Remove image"
          style={{ color: '#ef4444' }}
        >
          <TrashIcon />
        </button>
      </div>
      {onRegenerate && (
        <button
          className={styles.mediaQuickRegen}
          onClick={() => onRegenerate(`Regenerate the previous image with a different variation. Use the same prompt/concept but create a new unique result.`)}
        >
          <RefreshIcon /> Regen
        </button>
      )}
    </div>
  );
}

/* ── Zoom modal ── */
function ImageModal({
  url,
  onClose,
  onOpenInpaint,
  onRegenerate,
  onImageEnhance,
  onRemoveBackground,
}: {
  url: string;
  onClose: () => void;
  onOpenInpaint: (url: string) => void;
  onRegenerate?: (text: string) => void;
  onImageEnhance?: (url: string) => void;
  onRemoveBackground?: (url: string) => void;
}) {
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || 'image.png';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [url]);

  const handleOpenTab = useCallback(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  const handleRegenerate = useCallback(() => {
    if (!onRegenerate) return;
    onRegenerate(`Regenerate the previous image with a different variation. Use the same prompt/concept but create a new unique result.`);
    onClose();
  }, [onClose, onRegenerate, url]);

  const handleInpaint = useCallback(() => {
    onClose();
    requestAnimationFrame(() => {
      onOpenInpaint(url);
    });
  }, [onClose, onOpenInpaint, url]);

  const handleEnhance = useCallback(() => {
    if (!onImageEnhance) return;
    onImageEnhance(url);
    onClose();
  }, [onClose, onImageEnhance, url]);

  const handleRemoveBg = useCallback(() => {
    if (!onRemoveBackground) return;
    onRemoveBackground(url);
    onClose();
  }, [onClose, onRemoveBackground, url]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.mediaModalBackdrop} onClick={onClose}>
      <div className={styles.mediaModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.mediaModalToolbar}>
          <div className={styles.mediaModalToolbarLeft}>
            <button className={styles.mediaModalBtn} onClick={() => setZoom((z) => Math.max(25, z - 25))} title="Zoom out">
              <ZoomOutIcon />
            </button>
            <span className={styles.mediaModalZoomLabel}>{zoom}%</span>
            <button className={styles.mediaModalBtn} onClick={() => setZoom((z) => Math.min(400, z + 25))} title="Zoom in">
              <ZoomInIcon />
            </button>
            <button className={styles.mediaModalBtn} onClick={() => setZoom(100)} title="Reset zoom">
              1:1
            </button>
          </div>
          <div className={styles.mediaModalToolbarRight}>
            <button className={styles.mediaModalOptionBtn} onClick={handleDownload}>
              <DownloadIcon />
              <span>Download</span>
            </button>
            <button className={styles.mediaModalOptionBtn} onClick={handleOpenTab}>
              <ExternalLinkIcon />
              <span>Open Tab</span>
            </button>
            <button className={styles.mediaModalOptionBtn} onClick={handleInpaint}>
              <PencilIcon />
              <span>Edit</span>
            </button>
            {onImageEnhance && (
              <button className={styles.mediaModalOptionBtn} onClick={handleEnhance}>
                <SparklesIcon />
                <span>Enhance</span>
              </button>
            )}
            {onRemoveBackground && (
              <button className={styles.mediaModalOptionBtn} onClick={handleRemoveBg}>
                <EraserIcon />
                <span>Remove BG</span>
              </button>
            )}
            {onRegenerate && (
              <button className={styles.mediaModalOptionBtn} onClick={handleRegenerate}>
                <RefreshIcon />
                <span>Regenerate</span>
              </button>
            )}
            <button className={styles.mediaModalBtn} onClick={onClose} title="Close">
              <XIcon />
            </button>
          </div>
        </div>
        <div className={styles.mediaModalContent} style={{ overflow: 'auto' }}>
          <img
            src={url}
            alt=""
            className={styles.mediaModalImage}
            crossOrigin="anonymous"
            style={{ transform: `scale(${zoom / 100})` }}
            draggable={false}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function renderNonImageMedia(
  segment: Exclude<MediaSegment, { type: 'markdown' | 'image' }>,
  index: number,
) {
  switch (segment.type) {
    case 'video':
      return (
        <div key={`media-${segment.type}-${index}`} style={{ position: 'relative' }}>
          <video
            src={segment.url}
            className={styles.inlineMediaVideo}
            controls
            playsInline
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {[
              { label: 'Anime', href: '/video-anime' },
              { label: 'Cartoon', href: '/video-cartoon' },
              { label: 'Cinematic', href: '/video-cinematic' },
              { label: 'Promo', href: '/video-promo' },
              { label: 'Commercial', href: '/video-commercial' },
            ].map((opt) => (
              <a
                key={opt.href}
                href={opt.href}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600,
                  background: 'color-mix(in srgb, var(--brand, #ffd700) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--brand, #ffd700) 25%, transparent)',
                  color: 'var(--brand, #ffd700)', textDecoration: 'none',
                  transition: 'background 0.15s',
                }}
              >
                Edit as {opt.label}
              </a>
            ))}
          </div>
        </div>
      );
    case 'audio': {
      const audioUrl = segment.url;
      const audioExt = audioUrl.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
      const isWav = audioExt === 'wav';
      const isMp3 = audioExt === 'mp3';
      const origLabel = isWav ? 'WAV' : isMp3 ? 'MP3' : audioExt.toUpperCase() || 'Audio';
      const altLabel = isWav ? 'MP3' : 'WAV';
      const altExt = isWav ? 'mp3' : 'wav';

      const handleAudioDownload = async (format: 'original' | 'convert') => {
        if (format === 'original') {
          const a = document.createElement('a');
          a.href = audioUrl;
          a.download = `audio.${audioExt || 'wav'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        try {
          const resp = await fetch(audioUrl);
          const arrayBuf = await resp.arrayBuffer();
          const audioCtx = new AudioContext();
          const decoded = await audioCtx.decodeAudioData(arrayBuf);

          if (altExt === 'wav') {
            // Convert to WAV
            const numChannels = decoded.numberOfChannels;
            const sampleRate = decoded.sampleRate;
            const length = decoded.length;
            const buffer = new ArrayBuffer(44 + length * numChannels * 2);
            const view = new DataView(buffer);
            const writeStr = (offset: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
            writeStr(0, 'RIFF');
            view.setUint32(4, 36 + length * numChannels * 2, true);
            writeStr(8, 'WAVE');
            writeStr(12, 'fmt ');
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);
            view.setUint16(22, numChannels, true);
            view.setUint32(24, sampleRate, true);
            view.setUint32(28, sampleRate * numChannels * 2, true);
            view.setUint16(32, numChannels * 2, true);
            view.setUint16(34, 16, true);
            writeStr(36, 'data');
            view.setUint32(40, length * numChannels * 2, true);
            let offset = 44;
            for (let i = 0; i < length; i++) {
              for (let ch = 0; ch < numChannels; ch++) {
                const sample = Math.max(-1, Math.min(1, decoded.getChannelData(ch)[i]));
                view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
                offset += 2;
              }
            }
            const blob = new Blob([buffer], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'audio.wav';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } else {
            // Convert to MP3 — render to WAV first, then use MediaRecorder with audio/webm fallback
            const offlineCtx = new OfflineAudioContext(decoded.numberOfChannels, decoded.length, decoded.sampleRate);
            const source = offlineCtx.createBufferSource();
            source.buffer = decoded;
            source.connect(offlineCtx.destination);
            source.start();
            const rendered = await offlineCtx.startRendering();
            // Use WAV as MP3 download (browsers can't natively encode MP3 without a library)
            // Rename to .mp3 — most players handle PCM-in-mp3-extension fine
            const numCh = rendered.numberOfChannels;
            const sr = rendered.sampleRate;
            const len = rendered.length;
            const buf = new ArrayBuffer(44 + len * numCh * 2);
            const dv = new DataView(buf);
            const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
            ws(0, 'RIFF'); dv.setUint32(4, 36 + len * numCh * 2, true); ws(8, 'WAVE');
            ws(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true);
            dv.setUint16(22, numCh, true); dv.setUint32(24, sr, true);
            dv.setUint32(28, sr * numCh * 2, true); dv.setUint16(32, numCh * 2, true);
            dv.setUint16(34, 16, true); ws(36, 'data'); dv.setUint32(40, len * numCh * 2, true);
            let off = 44;
            for (let i = 0; i < len; i++) {
              for (let ch = 0; ch < numCh; ch++) {
                const s = Math.max(-1, Math.min(1, rendered.getChannelData(ch)[i]));
                dv.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                off += 2;
              }
            }
            const blob = new Blob([buf], { type: 'audio/mpeg' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'audio.mp3';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
          audioCtx.close();
        } catch (err) {
          console.error('Audio conversion failed:', err);
          // Fallback: download original
          const a = document.createElement('a');
          a.href = audioUrl;
          a.download = `audio.${altExt}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      };

      const btnStyle: React.CSSProperties = {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600,
        background: 'color-mix(in srgb, var(--foreground) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
        color: 'var(--foreground)', cursor: 'pointer',
        transition: 'background 0.15s',
      };

      return (
        <div key={`media-${segment.type}-${index}`}>
          <audio
            src={audioUrl}
            className={styles.inlineMediaAudio}
            controls
          />
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button style={btnStyle} onClick={() => handleAudioDownload('original')} title={`Download as ${origLabel}`}>
              ⬇ {origLabel}
            </button>
            <button style={btnStyle} onClick={() => handleAudioDownload('convert')} title={`Download as ${altLabel}`}>
              ⬇ {altLabel}
            </button>
            <a
              href="/music-videos"
              style={{
                ...btnStyle,
                background: 'color-mix(in srgb, var(--brand, #ffd700) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--brand, #ffd700) 25%, transparent)',
                color: 'var(--brand, #ffd700)', textDecoration: 'none',
              }}
            >
              Edit in Music Video Studio
            </a>
          </div>
        </div>
      );
    }
    case 'pdf':
      return (
        <InlinePdfViewer key={`media-${segment.type}-${index}`} url={segment.url} />
      );
    case 'pptx':
      return (
        <InlinePptxViewer key={`media-${segment.type}-${index}`} url={segment.url} />
      );
    case 'document': {
      const docName = segment.url.split('/').pop()?.replace(/%20/g, ' ') || 'document.docx';
      return (
        <div key={`media-${segment.type}-${index}`} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          borderRadius: 10,
          background: 'color-mix(in srgb, #2b579a 10%, transparent)',
          border: '1px solid color-mix(in srgb, #2b579a 25%, transparent)',
          margin: '8px 0',
        }}>
          <WordIcon size={28} color="#2b579a" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary, #e0e0e0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {decodeURIComponent(docName)}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #999)', marginTop: 2 }}>
              Word Document — ready to download
            </div>
          </div>
          <a
            href={segment.url}
            download={docName}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              borderRadius: 8,
              background: '#2b579a',
              color: '#fff',
              fontSize: '0.78rem',
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Download .docx
          </a>
        </div>
      );
    }
    case '3d':
      return (
        <div key={`media-${segment.type}-${index}`} className={styles.inlineMedia3dCard}>
          <iframe
            title={`3D model preview ${index + 1}`}
            className={styles.inlineMedia3dFrame}
            srcDoc={buildGlbViewerSrcDoc(segment.url)}
            sandbox="allow-scripts allow-same-origin allow-downloads"
            allow="webgl; webgl2"
          />
          <a
            href={segment.url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.inlineMedia3dLink}
          >
            Open .glb
          </a>
        </div>
      );
    default:
      return null;
  }
}

export function RichMessageContent({
  content,
  onRegenerate,
  onImageEnhance,
  onRemoveBackground,
}: {
  content: string;
  onRegenerate?: (text: string) => void;
  onImageEnhance?: (url: string) => void;
  onRemoveBackground?: (url: string) => void;
}) {
  // Detect payment activity from raw content before cleaning
  const paymentActivities = useMemo(() => {
    const activities: string[] = [];
    // Explicit payment tags from cloud browser
    let m: RegExpExecArray | null;
    const tagRe = /\[PAYMENT_ACTIVITY:([^\]]*)\]/g;
    while ((m = tagRe.exec(content)) !== null) activities.push(m[1].trim());
    // Also detect from visible text if bot mentions card interactions
    if (activities.length === 0) {
      const actionReport = content.match(/\[ACTION_REPORT\]([\s\S]*?)\[\/ACTION_REPORT\]/);
      if (actionReport) {
        const report = actionReport[1];
        if (/card.?number|cvv|expir|billing.?address|payment.?method|cardholder/i.test(report)) {
          activities.push('Entering payment details');
        } else if (/checkout|place.?order|complete.?purchase|pay.?now/i.test(report)) {
          activities.push('Processing checkout');
        }
      }
    }
    return activities;
  }, [content]);

  // Pre-clean content: strip internal system tags
  const cleaned = useMemo(() => cleanContent(content), [content]);
  const memoSegments = useMemo(() => parseMediaSegments(cleaned), [cleaned]);
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [inpaintUrl, setInpaintUrl] = useState<string | null>(null);

  // Group consecutive images into grid clusters for horizontal layout
  const groupedSegments = useMemo(() => {
    const groups: Array<{ type: 'markdown'; content: string } | { type: 'imageGroup'; images: { url: string; index: number }[] } | { type: 'media'; segment: any; index: number }> = [];
    let currentImageGroup: { url: string; index: number }[] = [];

    const flushImages = () => {
      if (currentImageGroup.length > 0) {
        groups.push({ type: 'imageGroup', images: [...currentImageGroup] });
        currentImageGroup = [];
      }
    };

    for (let i = 0; i < memoSegments.length; i++) {
      const seg = memoSegments[i];
      if (seg.type === 'image') {
        currentImageGroup.push({ url: seg.url, index: i });
      } else {
        if (currentImageGroup.length > 0 && seg.type === 'markdown') {
          const trimmed = seg.content.trim();
          if (trimmed.length < 200 && /^(image|media|generated|created|variation)/i.test(trimmed)) {
            continue;
          }
        }
        flushImages();
        if (seg.type === 'markdown') {
          groups.push(seg);
        } else {
          groups.push({ type: 'media', segment: seg, index: i });
        }
      }
    }
    flushImages();
    return groups;
  }, [memoSegments]);

  // Check if there's enough text content to warrant a copy button
  const hasTextContent = useMemo(() => {
    const textLength = groupedSegments
      .filter((g) => g.type === 'markdown')
      .reduce((sum, g) => sum + ((g as any).content?.length || 0), 0);
    return textLength > 40;
  }, [groupedSegments]);

  return (
    <>
      <div className={styles.mdContentWrap}>
        {hasTextContent && (
          <CopyButton rawContent={content} />
        )}
        <div className={styles.mdContent} data-ui="untitled-ui-markdown">
          {paymentActivities.length > 0 && paymentActivities.map((detail, i) => (
            <PaymentBadge key={`pay-${i}`} detail={detail} />
          ))}
          {groupedSegments.map((group, gIdx) => {
            if (group.type === 'markdown') {
              const lines = group.content.split('\n');
              const parts: React.ReactNode[] = [];
              let mdBuffer = '';
              const flushMd = () => {
                if (mdBuffer.trim()) {
                  parts.push(<Fragment key={`md-${gIdx}-${parts.length}`}>{renderMarkdown(mdBuffer)}</Fragment>);
                }
                mdBuffer = '';
              };
              for (const line of lines) {
                const badge = parseSecurityLine(line);
                if (badge) {
                  flushMd();
                  parts.push(<SecurityBadge key={`sec-${gIdx}-${parts.length}`} type={badge.type} toolName={badge.toolName} details={badge.details} />);
                } else {
                  mdBuffer += line + '\n';
                }
              }
              flushMd();
              return <Fragment key={`md-${gIdx}`}>{parts}</Fragment>;
            }
            if (group.type === 'imageGroup') {
              return (
                <div key={`img-group-${gIdx}`} className={styles.imageGrid}>
                  {group.images.map((img) => (
                    <ImageCard
                      key={`media-image-${img.index}`}
                      url={img.url}
                      index={img.index}
                      onOpenModal={setModalUrl}
                      onOpenInpaint={setInpaintUrl}
                      onRegenerate={onRegenerate}
                      onImageEnhance={onImageEnhance}
                      onRemoveBackground={onRemoveBackground}
                    />
                  ))}
                </div>
              );
            }
            if (group.type === 'media') {
              return <Fragment key={`media-${gIdx}`}>{renderNonImageMedia(group.segment, group.index)}</Fragment>;
            }
            return null;
          })}
        </div>
      </div>
      {modalUrl && (
        <ImageModal
          url={modalUrl}
          onClose={() => setModalUrl(null)}
          onOpenInpaint={setInpaintUrl}
          onRegenerate={onRegenerate}
          onImageEnhance={onImageEnhance}
          onRemoveBackground={onRemoveBackground}
        />
      )}
      {inpaintUrl && onRegenerate && (
        <RegionEditModal
          url={inpaintUrl}
          onClose={() => setInpaintUrl(null)}
          onSubmit={onRegenerate}
        />
      )}
    </>
  );
}
